/**
 * Recommendation Engine for Seoulskin AI Skin Test
 * Analyzes questionnaire responses and image results to match products and build personalized routines
 */

class RecommendationEngine {
  constructor(config = {}) {
    // Collection handles mapping (matches your Shopify collections)
    this.collectionMap = config.collectionMap || {
      'acne': 'acne',
      'pigmentation': 'pigmentation',
      'anti-aging': 'anti-aging',
      'hydration': 'hydration',
      'pore': 'pore',
      'barrier-repair': 'barrier-repair',
    };

    // Product handles mapping for each concern (CUSTOMIZE THESE)
    this.productHandlesMap = config.productHandlesMap || {
      'acne': [
        'seoulskin-zero-pore-pads',
        'seoulskin-heartleaf-pore-deep-cleansing-foamheartleaf-bha',
        'seoulskin-korean-face-cleansing-oil-150-ml'
      ],
      'pigmentation': [
        'seoulskin-kojic-rice-water-brightening-soap-pack-of-3',
        'seoulskin-snail-mucin-micro-needling-serum-7500-30ml',
        'seoulskin-heartleaf-pore-deep-cleansing-foamheartleaf-bha'
      ],
      'anti-aging': [
        'seoulskin-collagen-glass-skin-mask',
        'seoulskin-snail-mucin-micro-needling-serum-7500-30ml',
        'seoulskin-snail-mucin-under-eye-patches'
      ],
      'hydration': [
        'seoulskin-collagen-glass-skin-mask',
        'seoulskin-snail-mucin-under-eye-patches',
        'seoulskin-zero-pore-pads'
      ],
      'pore': [
        'seoulskin-korean-face-cleansing-oil-150-ml',
        'seoulskin-heartleaf-pore-deep-cleansing-foamheartleaf-bha',
        'seoulskin-zero-pore-pads'
      ],
      'barrier-repair': [
        'seoulskin-snail-mucin-micro-needling-serum-7500-30ml',
        'seoulskin-heartleaf-pore-deep-cleansing-foamheartleaf-bha',
        'seoulskin-kojic-rice-water-brightening-soap-pack-of-3'
      ]
    };

    // Configurable weights for scoring
    this.weights = config.weights || {
      skinType: 0.3,
      concerns: 0.4,
      age: 0.1,
      routine: 0.1,
      goals: 0.1
    };

    // Routine steps mapping
    this.routineSteps = ['cleanser', 'toner', 'treatment', 'moisturizer', 'spf'];
    
    // Product type keywords for routine mapping
    this.productTypeKeywords = {
      'cleanser': ['cleanser', 'cleansing', 'wash', 'foam', 'oil'],
      'toner': ['toner', 'essence', 'mist'],
      'treatment': ['serum', 'treatment', 'ampoule', 'concentrate'],
      'moisturizer': ['moisturizer', 'cream', 'lotion', 'gel'],
      'spf': ['sunscreen', 'spf', 'sun', 'uv']
    };
  }

  /**
   * Analyze questionnaire responses with weighted scoring
   */
  analyzeQuestionnaire(questionnaire) {
    const scores = {
      'acne': 0,
      'pigmentation': 0,
      'anti-aging': 0,
      'hydration': 0,
      'pore': 0,
      'barrier-repair': 0
    };

    // Skin type weight (configurable, default: 30%)
    const skinTypeWeight = this.weights.skinType;
    if (questionnaire.skinType === 'oily') {
      scores['acne'] += 30 * skinTypeWeight;
      scores['pore'] += 25 * skinTypeWeight;
    } else if (questionnaire.skinType === 'dry') {
      scores['hydration'] += 30 * skinTypeWeight;
      scores['barrier-repair'] += 25 * skinTypeWeight;
    } else if (questionnaire.skinType === 'combination') {
      scores['acne'] += 15 * skinTypeWeight;
      scores['pore'] += 15 * skinTypeWeight;
      scores['hydration'] += 15 * skinTypeWeight;
    }

    // Primary concerns weight (configurable, default: 40%)
    const concernsWeight = this.weights.concerns;
    const userConcerns = questionnaire.concerns || questionnaire.skinConcerns || [];
    console.log('Processing concerns from questionnaire:', userConcerns);
    
    if (userConcerns && userConcerns.length > 0) {
      userConcerns.forEach(concern => {
        const concernKey = concern.toLowerCase().replace(/\s+/g, '-');
        if (scores[concernKey] !== undefined) {
          scores[concernKey] += 40 * concernsWeight;
        }
      });
    }

    // Age weight (configurable, default: 10%)
    const ageWeight = this.weights.age;
    const age = parseInt(questionnaire.age) || 25;
    if (age >= 30) {
      scores['anti-aging'] += 30 * ageWeight;
      scores['wrinkles'] += 25 * ageWeight;
    } else if (age < 20) {
      scores['acne'] += 20 * ageWeight;
    }

    // Current routine weight (configurable, default: 10%)
    const routineWeight = this.weights.routine;
    if (questionnaire.currentRoutine === 'none' || questionnaire.currentRoutine === 'basic') {
      scores['barrier-repair'] += 20 * routineWeight;
      scores['hydration'] += 15 * routineWeight;
    }

    // Goals weight (configurable, default: 10%)
    const goalsWeight = this.weights.goals;
    if (questionnaire.goals) {
      questionnaire.goals.forEach(goal => {
        const goalKey = goal.toLowerCase().replace(/\s+/g, '-');
        if (scores[goalKey] !== undefined) {
          scores[goalKey] += 10 * goalsWeight;
        }
      });
    }

    return scores;
  }

  /**
   * Process image analysis results from Gemini API
   */
  processImageAnalysis(imageResults) {
    const scores = {
      'acne': 0,
      'pigmentation': 0,
      'pore': 0,
      'anti-aging': 0,
      'hydration': 0,
      'barrier-repair': 0
    };

    if (!imageResults) return scores;

    console.log('🔍 Processing Gemini AI analysis:', imageResults);

    // Gemini scores: 0-100 where higher = better skin condition
    // Map Gemini response fields to our 6 concern keys
    scores['acne'] = imageResults.acne || 0;
    scores['pore'] = imageResults.pore || 0;
    scores['pigmentation'] = imageResults.pigmentation || 0;
    scores['anti-aging'] = imageResults.wrinkles || 0;
    scores['hydration'] = imageResults.hydration || 0;
    scores['barrier-repair'] = imageResults['barrier-repair'] || imageResults.barrier_health || 0;
    
    console.log('✅ Direct scores from Gemini (0-100, higher=better):', scores);
    return scores;
  }

  /**
   * Combine questionnaire and image analysis scores
   * For display purposes, use Gemini scores directly (they're already 0-100)
   * For priority ranking, add questionnaire concerns as boost
   */
  combineScores(questionnaireScores, imageScores) {
    // Use Gemini scores as base (0-100 where higher = better)
    const combined = { ...imageScores };
    
    // Boost priority for user-selected concerns (for ranking only)
    // This doesn't change the display percentage, just priority order
    Object.keys(questionnaireScores).forEach(key => {
      if (questionnaireScores[key] > 20 && combined[key] !== undefined) {
        // User selected this concern - mark it as important for priority
        // But don't change the actual score
        combined[`${key}_priority`] = true;
      }
    });

    return combined;
  }

  /**
   * Determine primary and secondary concerns with priorities
   * For Gemini scores: lower score = worse condition = higher priority
   */
  determineConcerns(scores) {
    // Filter out priority markers
    const actualScores = {};
    Object.keys(scores).forEach(key => {
      if (!key.includes('_priority')) {
        actualScores[key] = scores[key];
      }
    });
    
    // Sort by LOWEST score (worse condition = higher priority)
    const sorted = Object.entries(actualScores)
      .sort((a, b) => a[1] - b[1]) // Lower score first
      .filter(([key, value]) => value < 80); // Only show concerns below 80%

    const primary = sorted[0] ? sorted[0][0] : 'hydration';
    const secondary = sorted[1] ? sorted[1][0] : 'barrier-repair';
    
    // Get top 3-5 concerns as priorities (lowest scores = biggest concerns)
    const priorities = [];
    for (let i = 0; i < Math.min(5, sorted.length); i++) {
      const [concern, score] = sorted[i];
      priorities.push({
        concern,
        score,
        level: i === 0 ? 'high' : i === 1 ? 'medium' : 'low'
      });
    }

    return { primary, secondary, priorities, allScores: actualScores };
  }

  /**
   * Calculate skin age based on analysis
   * Gemini scores: 0-100 where higher = better
   */
  calculateSkinAge(questionnaire, imageResults, scores) {
    const baseAge = parseInt(questionnaire.age) || 25;
    
    // Use Gemini's estimate if available
    if (imageResults && imageResults.skin_age_estimate) {
      return imageResults.skin_age_estimate;
    }
    
    // Calculate based on scores (lower scores = worse skin = older)
    let ageAdjustment = 0;
    
    if (scores['anti-aging'] < 70) ageAdjustment += 3;
    if (scores['wrinkles'] < 70) ageAdjustment += 2;
    if (scores['pigmentation'] < 70) ageAdjustment += 2;
    if (scores['hydration'] < 70) ageAdjustment += 1;

    return Math.max(18, Math.min(60, baseAge + ageAdjustment));
  }

  /**
   * Calculate overall skin score
   * Gemini scores: 0-100 where higher = better
   */
  calculateSkinScore(scores, imageResults) {
    // Use Gemini's overall quality if available
    if (imageResults && imageResults.overall_skin_quality) {
      return imageResults.overall_skin_quality;
    }
    
    // Calculate average of all scores (0-100 where higher = better)
    const validScores = Object.values(scores).filter(v => typeof v === 'number' && v > 0);
    
    if (validScores.length === 0) return 75; // Default
    
    const avgScore = validScores.reduce((sum, val) => sum + val, 0) / validScores.length;
    
    return Math.round(Math.max(0, Math.min(100, avgScore)));
  }

  /**
   * Map products to routine steps
   */
  mapProductsToRoutine(products) {
    const routine = {
      cleanser: null,
      toner: null,
      treatment: [],
      moisturizer: null,
      spf: null
    };

    products.forEach(product => {
      const title = (product.title || '').toLowerCase();
      const productType = product.product_type ? product.product_type.toLowerCase() : '';
      const tags = (product.tags || []).map(t => t.toLowerCase());

      // Check for cleanser
      if (!routine.cleanser && (
        this.productTypeKeywords.cleanser.some(kw => title.includes(kw) || productType.includes(kw))
      )) {
        routine.cleanser = product;
        return;
      }

      // Check for toner
      if (!routine.toner && (
        this.productTypeKeywords.toner.some(kw => title.includes(kw) || productType.includes(kw))
      )) {
        routine.toner = product;
        return;
      }

      // Check for moisturizer
      if (!routine.moisturizer && (
        this.productTypeKeywords.moisturizer.some(kw => title.includes(kw) || productType.includes(kw))
      )) {
        routine.moisturizer = product;
        return;
      }

      // Check for SPF
      if (!routine.spf && (
        this.productTypeKeywords.spf.some(kw => title.includes(kw) || productType.includes(kw))
      )) {
        routine.spf = product;
        return;
      }

      // Treatment (can have multiple)
      if (this.productTypeKeywords.treatment.some(kw => title.includes(kw) || productType.includes(kw))) {
        routine.treatment.push(product);
      }
    });

    return routine;
  }

  /**
   * Main analysis function
   */
  async analyze(questionnaire, imageResults = null) {
    // Analyze questionnaire
    const questionnaireScores = this.analyzeQuestionnaire(questionnaire);

    // Process image analysis
    const imageScores = this.processImageAnalysis(imageResults);

    // Combine scores
    const combinedScores = this.combineScores(questionnaireScores, imageScores);

    // Determine concerns
    const { primary, secondary, priorities, allScores } = this.determineConcerns(combinedScores);

    // Calculate skin age and score
    const skinAge = this.calculateSkinAge(questionnaire, imageResults, combinedScores);
    const skinScore = this.calculateSkinScore(combinedScores, imageResults);

    // ✅ CHANGED: Fetch products by handles for PRIMARY concern ONLY
    const primaryProducts = await this.fetchProductsByHandles(primary);

    // Map products to routine
    const routine = this.mapProductsToRoutine(primaryProducts);

    // ✅ CHANGED: Get products by handles for PRIMARY concern only
    const productsByPriority = {};
    productsByPriority[primary] = {
      products: primaryProducts,
      score: allScores[primary] || 50,
      level: 'high'
    };

    return {
      primaryConcern: primary,
      secondaryConcern: secondary,
      skinAge: skinAge,
      skinScore: skinScore,
      skinType: questionnaire.skinType,
      concerns: allScores,  // ✅ ADD THIS - the concern scores
      scores: allScores,     // ✅ ADD THIS - alternative field name
      priorities: priorities, // ✅ ADD THIS
      productsByPriority: productsByPriority, // ✅ CHANGED - Only primary concern products
      metrics: imageResults || {
        pores: 0,
        pigmentation: 0,
        wrinkles: 0,
        texture: 0
      },
      routine: routine,
      recommendedProducts: primaryProducts // ✅ CHANGED - Only primary concern products
    };
  }

  /**
   * Fetch products from Shopify collections
   */
  async fetchProductsFromCollections(concerns) {
    const allProducts = [];

    for (const concern of concerns) {
      const collectionHandle = this.collectionMap[concern] || concern;
      
      try {
        // Try to fetch from Shopify Storefront API
        const response = await fetch(`/collections/${collectionHandle}/products.json`);
        if (response.ok) {
          const data = await response.json();
          if (data.products) {
            allProducts.push(...data.products);
          }
        }
      } catch (error) {
        console.warn(`Failed to fetch collection ${collectionHandle}:`, error);
      }
    }

    // Remove duplicates
    const uniqueProducts = [];
    const seenIds = new Set();
    
    allProducts.forEach(product => {
      if (!seenIds.has(product.id)) {
        seenIds.add(product.id);
        uniqueProducts.push(product);
      }
    });

    return uniqueProducts;
  }

  /**
   * Fetch products by handles for a specific concern
   */
  async fetchProductsByHandles(concern) {
    const handles = this.productHandlesMap[concern] || [];
    const products = [];

    console.log(`Fetching products for concern: ${concern}`, handles);

    for (const handle of handles) {
      try {
        // Fetch product by handle from Shopify
        const response = await fetch(`/products/${handle}.js`);
        if (response.ok) {
          const product = await response.json();
          products.push(product);
        } else {
          console.warn(`Product not found: ${handle}`);
        }
      } catch (error) {
        console.warn(`Failed to fetch product ${handle}:`, error);
      }
    }

    console.log(`Fetched ${products.length} products for ${concern}`);
    return products;
  }
}

// Export for use in other scripts
if (typeof module !== 'undefined' && module.exports) {
  module.exports = RecommendationEngine;
}

